import { inject, Injectable } from '@angular/core';
import dayjs from 'dayjs';
import { EMPTY_GUID } from '../../../shared/constants/global';
import {
  BomLineNumbering,
  SupplierPoInfo,
  SyntheticPartSearchRow,
  TaskDetail,
  TaxonomyLevels,
  vcFGComponent,
} from '../vas-configurator.constants';
import { GlobalService } from '../../../shared/services/global.service';
import { Calculation } from '../../../shared/services/calculation';
import { VcResolver } from './vc-resolver';

@Injectable({
  providedIn: 'root',
})
export class VcPayload {
  private globalService = inject(GlobalService);
  private calculator = inject(Calculation);
  private resolver = inject(VcResolver);
  public MaxDecimals = 4;

  // ── Payload builders ────────────────────────────────────────────────────────

  transformBOMData(
    header: any,
    components: any[],
    siteList: any[],
    shipToList: any[],
    isFGReadOnly: boolean,
    fgPartTypes: any[],
  ) {
    return {
      FGHeader: {
        FinishedGoodId: header.FinishedGoodId,
        FGPartNumber: header.FGPartNumber,
        Description: header.Description,
        Version: header.Version,
        TransactiblePartNumber: header.TransactiblePartNumber,
        BOMType: header.BOMType,
        Status: header.Status,
        OpsApprovalObtained: header.OpsApprovalObtained,
        BOMEditAllowed: header.BOMEditAllowed,
        WorkInstructionId: header.WorkInstructionId,
        FGLeadTimeInDays: header.FGLeadTimeInDays,
        CutAssembly: header.CutAssembly,
        OSPSiteId:
          siteList?.find(
            (val: any) =>
              val.SupplierNumber === header.OSPSiteId || val.SupplierSiteId === header.OSPSiteId,
          )?.SupplierSiteId || null,
        OSPShipToId: isFGReadOnly
          ? header.OSPShipToId
          : shipToList?.find((val: any) => val.AccNum === header.OSPShipToId)?.CId || null,
        WorkOrderRelease: header.WorkOrderRelease,
        UOM: header.UOM,
        ShipType: this.resolver.resolveShipTypeToEnumValue(header.ShipType),
        PartType: this.resolver.resolvePartTypeToEnumValue(header.PartType, fgPartTypes),
        ShipSelction: header.ShipSelction,
        ShipLocation: this.resolver.getNumberFromLocationName(header.ShipLocation),
        EarliestAcceptDate: header.EarliestAcceptDate,
        RequiredOnSiteDate: header.RequiredOnSiteDate,
        WarehouseDate: header.WarehouseDate,
        CustLineNum: header.CustLineNum,
      },
      FGLineItems: components.map((item: any) => ({
        FinishedGoodLineItemId: item.FinishedGoodLineItemId,
        FinishedGoodId: item.FinishedGoodId,
        PartNumber: item.PartNumber,
        MFRPartNumber: item.MFRPartNumber,
        ManufacturerId: item.ManufacturerId ?? null,
        ManufacturerName: item.ManufacturerName ?? null,
        Description: item.Description ?? null,
        Quantity: item.Quantity ?? null,
        ShipType: this.resolver.resolveShipTypeToEnumValue(item.ShipType),
        ShipLocationId: item.ShipLocationId ?? null,
        InventoryType: item.InventoryType,
        PartType: this.resolver.resolvePartTypeToEnumValue(item.PartType, fgPartTypes),
        UOM: item.UOM,
        QuotedCost: this.globalService.getOnlyValue(item.QuotedCost),
        SupplierQuoteNumber: item.SupplierQuoteNumber,
        QuotedUOM: item.QuotedUOM,
        QuotedEffectiveDate: item.QuotedEffectiveDate,
        SupplierQuoteValidTillDate: item.SupplierQuoteValidTillDate,
        SupplierSiteId:
          siteList?.find(
            (val) =>
              val.SupplierNumber === item.SupplierSiteId ||
              val.SupplierSiteId === item.SupplierSiteId,
          )?.SupplierSiteId || null,
        StageGroupNumber: item.StageGroupNumber ?? null,
        StageDependencies: item.StageDependencies ?? null,
        TaskTypeId: item.TaskTypeId ?? null,
        TaskTypeName: item.TaskTypeName ?? null,
        TaskTypeCode: item.TaskTypeCode ?? null,
        LBRPartTypeTaskAttributes: item.LBRPartTypeTaskAttributes ?? null,
      })),
    };
  }

  buildSaveFgPayload(
    header: any,
    components: any[],
    isCreate: boolean,
    siteList: any[],
    shipToList: any[],
    isFGReadOnly: boolean,
    fgPartTypes: any[],
  ) {
    const fgId = isCreate ? EMPTY_GUID : header.FinishedGoodId;
    return {
      FGHeader: {
        FinishedGoodId: fgId,
        ParentFinishedGoodId: isCreate ? (header.FinishedGoodId ?? null) : null,
        FGPartNumber: header.FGPartNumber,
        Description: header.Description,
        Version: header.Version,
        TransactiblePartNumber: isCreate ? null : (header.TransactiblePartNumber ?? null),
        BOMType: header.BOMType,
        Status: header.Status,
        OpsApprovalObtained: header.OpsApprovalObtained,
        BOMEditAllowed: header.BOMEditAllowed,
        WorkInstructionId: header.WorkInstructionId,
        FGLeadTimeInDays: header.FGLeadTimeInDays,
        CutAssembly: header.CutAssembly,
        OSPSiteId:
          siteList?.find(
            (val: any) =>
              val.SupplierNumber === header.OSPSiteId || val.SupplierSiteId === header.OSPSiteId,
          )?.SupplierSiteId || null,
        OSPShipToId: isFGReadOnly
          ? header.OSPShipToId
          : shipToList?.find((val: any) => val.AccNum === header.OSPShipToId)?.CId || null,
        WorkOrderRelease: header.WorkOrderRelease,
        UOM: header.UOM,
        ShipType: this.resolver.resolveShipTypeToEnumValue(header.ShipType),
        PartType: this.resolver.resolvePartTypeToEnumValue(header.PartType, fgPartTypes),
        ShipSelction: header.ShipSelction ?? null,
        ShipLocation: this.resolver.getNumberFromLocationName(header.ShipLocation),
        ShipLocationIds: header.ShipLocationIds ?? [],
        WarehouseDate: header.WarehouseDate ?? null,
        CustLineNum: header.CustLineNum ?? null,
        SalesOrderNumber: header.salesOrderNumber ?? null,
        DefaultShipLocationId: header.DefaultShipLocationId ?? null,
        AccountNumber: header.customerAccountNumber ?? null,
      },
      FGLineItems: components.map((item: any) => ({
        FinishedGoodLineItemId: isCreate ? EMPTY_GUID : (item.FinishedGoodLineItemId ?? EMPTY_GUID),
        FinishedGoodId: isCreate ? EMPTY_GUID : (item.FinishedGoodId ?? fgId),
        LineNumber: this.resolver.toNumberOrNull(item.LineNumber),
        PartNumber: item.PartNumber,
        MFRPartNumber: item.MFRPartNumber,
        ManufacturerId: item.ManufacturerId ?? null,
        ManufacturerName: item.ManufacturerName ?? null,
        Description: item.Description ?? null,
        Quantity: item.Quantity ?? null,
        ShipType: this.resolver.resolveShipTypeToEnumValue(item.ShipType),
        ShipLocationId: item.ShipLocationId ?? null,
        InventoryType: item.InventoryType,
        PartType: this.resolver.resolvePartTypeToEnumValue(item.PartType, fgPartTypes),
        UOM: item.UOM,
        QuotedCost: this.globalService.getOnlyValue(item.QuotedCost),
        SupplierQuoteNumber: item.SupplierQuoteNumber,
        QuotedUOM: item.QuotedUOM,
        QuotedEffectiveDate: item.QuotedEffectiveDate,
        SupplierQuoteValidTillDate: item.SupplierQuoteValidTillDate,
        SupplierSiteId:
          siteList?.find(
            (val) =>
              val.SupplierNumber === item.SupplierSiteId ||
              val.SupplierSiteId === item.SupplierSiteId,
          )?.SupplierSiteId || null,
        CustomerPartNumber: item.CustomerPartNumber ?? null,
        TaxonomyId: item.TaxonomyId ?? null,
        TaxonomyL1: item.TaxonomyL1 ?? null,
        TaxonomyL1Name: item.TaxonomyL1Name ?? null,
        TaxonomyL2: item.TaxonomyL2 ?? null,
        TaxonomyL2Name: item.TaxonomyL2Name ?? null,
        TaxonomyL3: item.TaxonomyL3 ?? null,
        TaxonomyL3Name: item.TaxonomyL3Name ?? null,
        TaxonomyL4Name: item.TaxonomyL4Name ?? null,
        StageGroupNumber: item.StageGroupNumber ?? null,
        StageDependencies: item.StageDependencies ?? null,
        TaskTypeId: item.TaskTypeId ?? null,
        TaskTypeName: item.TaskTypeName ?? null,
        TaskTypeCode: item.TaskTypeCode ?? null,
        LBRPartTypeTaskAttributes: item.LBRPartTypeTaskAttributes ?? null,
      })),
    };
  }

  buildHostResponse(
    requestHeader: any,
    fgHeader: any,
    components: any[] = [],
    siteList: any[],
    lineNumbering: { root: BomLineNumbering; componentMap: Map<string, BomLineNumbering> },
    message?: string | null,
  ): any {
    const fgPricingInfo = this.resolver.parseJsonField(fgHeader.pricingInfo);
    if (fgPricingInfo) {
      fgPricingInfo['supplierSite'] = this.resolveSupplierNumber(fgHeader.SupplierSiteId, siteList);
      fgPricingInfo['standardCost'] = this.globalService.getOnlyValue(fgHeader.Cost);
      fgPricingInfo['sellPrice'] = this.globalService.getOnlyValue(fgHeader.Price);
      fgPricingInfo['averageCost'] = this.globalService.getOnlyValue(fgHeader.AverageCost);
    }
    const bomType = fgHeader?.BOMType ?? null;
    return {
      quantity: fgHeader?.Quantity != null ? Number(fgHeader.Quantity) : null,
      shipSelection: fgHeader?.ShipSelction ?? null,
      earliestAcceptDate: fgHeader?.EarliestAcceptDate ?? null,
      requiredOnsiteDate: fgHeader?.RequiredOnSiteDate ?? null,
      warehouseDate: fgHeader?.WarehouseDate ?? null,
      customerLineNumber: fgHeader?.CustLineNum ?? null,
      transferFromShipLocation: fgHeader?.TransferFromShipLocation ?? null,
      fgCutAssemblyRequired: fgHeader?.CutAssembly ?? null,
      shipType: this.resolver.resolveShipTypeDescription(fgHeader?.ShipType ?? null),
      productInfo: this.resolver.parseJsonField(fgHeader.PartJson),
      inventoryInfo: this.resolver.parseJsonField(fgHeader.InventoryJson),
      orgAttributes: this.resolver.parseJsonField(fgHeader.OrgJson),
      shipLocationShipType: fgHeader?.shipLocationShipType ?? 'Non-STK',
      ...(fgPricingInfo ? { pricingInfo: fgPricingInfo } : {}),
      salesOrderLineNumber: lineNumbering.root.salesOrderLineNumber,
      parentSalesOrderLineNumber: lineNumbering.root.parentSalesOrderLineNumber,
      bomLevel: lineNumbering.root.bomLevel,
      fgTaskDetail: this.buildHeaderTaskDetail(fgHeader, bomType),
      kitComponents: (components ?? []).map((comp: any) => {
        const parsedPricingInfo = this.resolver.parseJsonField(comp.pricingInfo);
        if (parsedPricingInfo) {
          parsedPricingInfo['supplierSite'] = this.resolveSupplierNumber(
            comp.SupplierSiteId,
            siteList,
          );
          parsedPricingInfo['standardCost'] = this.globalService.getOnlyValue(comp.UnitCost);
          parsedPricingInfo['sellPrice'] = this.globalService.getOnlyValue(comp.UnitPrice);
          if (this.resolver.isNestedFgComponent(comp)) {
            parsedPricingInfo['averageCost'] = this.globalService.getOnlyValue(comp.AverageCost);
          }
        }
        const numbering = lineNumbering.componentMap.get(comp.FinishedGoodLineItemId ?? '') ?? {
          salesOrderLineNumber: null,
          parentSalesOrderLineNumber: null,
          bomLevel: 1,
        };
        return {
          kitConfigQty:
            comp.Quantity != null && !isNaN(Number(comp.Quantity)) ? Number(comp.Quantity) : null,
          quantity: comp.orderQuantity ?? null,
          gpPercentage: this.resolver.normalizeGP(comp.GP ?? comp.BM),
          inventoryType: this.resolver.resolveInventoryTypeDescription(comp.InventoryType),
          inventorySegment: comp.InventorySegment ?? null,
          inventorySegmentType: comp.InventorySegmentType ?? null,
          transferFromShipLocation: comp.TransferFromShipLocation ?? null,
          shipType: this.resolver.resolveShipTypeDescription(comp.ShipType ?? null),
          productInfo: this.resolver.parseJsonField(comp.PartJson),
          inventoryInfo: this.resolver.parseJsonField(comp.InventoryJson),
          orgAttributes: this.resolver.parseJsonField(comp.OrgJson),
          shipLocationShipType: comp.shipLocationShipType ?? 'Non-STK',
          salesOrderLineNumber: numbering.salesOrderLineNumber,
          parentSalesOrderLineNumber: numbering.parentSalesOrderLineNumber,
          bomLevel: numbering.bomLevel,
          fgTaskDetail: this.buildComponentTaskDetail(comp, bomType),
          supplierInfo: this.buildSupplierInfoPayload(comp, siteList),
          ...(parsedPricingInfo
            ? { pricingAPIDataCall: true, pricingInfo: parsedPricingInfo }
            : {}),
        };
      }),
    };
  }

  // ── RVA task-detail (stage grouping) ────────────────────────────────────────

  buildComponentTaskDetail(comp: any, bomType: string | null): TaskDetail | null {
    const isRva = bomType === 'RepeatableValueAdd';
    if (!isRva) {
      return null;
    }
    const stageGroupId = comp?.StageGroupNumber ?? null;
    return {
      stageGroupId,
      // TBD: sequence depends on dependency ordering not yet modeled — left null until designed.
      stageSequence: null,
      stageDependencies: stageGroupId != null ? (comp?.StageDependencies ?? []) : null,
      // taskType carries TaskTypeCode (e.g. "STRIPE"), not TaskTypeName/TaskTypeId — CPQ wants the code.
      taskType: comp?.TaskTypeCode ?? null,
      taskAttributes: comp?.LBRPartTypeTaskAttributes ?? null,
    };
  }

  buildHeaderTaskDetail(fgHeader: any, bomType: string | null): TaskDetail | null {
    if (bomType !== 'RepeatableValueAdd') {
      return null;
    }
    // The backend already resolves the FG-level TaskTypeCode (including the "MIXED" case
    // across staged components) — pass it through rather than re-deriving it client-side.
    return {
      stageGroupId: null,
      stageSequence: null,
      stageDependencies: null,
      taskType: fgHeader?.TaskTypeCode ?? null,
      taskAttributes: null,
    };
  }

  generateSyntheticPartAndInventoryData(
    fgDetails: any,
    taxonomy?: TaxonomyLevels | null,
  ): {
    PartJson: string;
    InventoryJson: string;
    pricingInfo: string;
  } {
    return this.assembleSyntheticPartSearchData({
      partNumber: fgDetails.TransactiblePartNumber ?? null,
      partDescription: fgDetails.Description ?? null,
      mfgPartNumber: fgDetails.FGPartNumber ?? null,
      manufacturerName: fgDetails.businessUnit ?? null,
      manufacturerCode: fgDetails.businessUnit ?? null,
      uom: fgDetails.UOM ?? null,
      partType: fgDetails.PartType ?? null,
      shipType: fgDetails.ShipType ?? null,
      shipLocation: fgDetails.ShipLocation ?? null,
      quantity: fgDetails.Quantity ?? null,
      lineReference: fgDetails.FinishedGoodId ?? null,
      ...(taxonomy ?? {}),
    });
  }

  // Component T-Parts only need a synthetic PartJson — InventoryJson/pricingInfo for
  // components still come from the real getInventoryLineitemDetails/getPricingDetails APIs.
  generateSyntheticPartJsonForComponent(
    item: any,
    addLineItem: vcFGComponent,
    taxonomy?: TaxonomyLevels | null,
  ): string {
    return JSON.stringify(
      this.buildSyntheticPartJson({
        partNumber: addLineItem.PartNumber ?? null,
        partDescription: addLineItem.Description ?? null,
        mfgPartNumber: addLineItem.MFRPartNumber ?? null,
        manufacturerName: addLineItem.ManufacturerName ?? null,
        manufacturerCode: item.ManufacturerId ?? addLineItem.ManufacturerName ?? null,
        uom: addLineItem.UOM ?? null,
        // Use the raw item's PartType (descriptive text, e.g. "OSP") — addLineItem.PartType
        // has already been converted to a numeric id and would break the shared shape.
        partType: item.PartType ?? null,
        shipType: addLineItem.ShipType ?? null,
        shipLocation: addLineItem.ShipLocation ?? null,
        quantity: addLineItem.Quantity ?? null,
        lineReference: addLineItem.FinishedGoodLineItemId ?? null,
        ...(taxonomy ?? {}),
      }),
    );
  }

  // Shared core: composes the three JSON blobs below from one normalized row.
  // Used by generateSyntheticPartAndInventoryData (FG header — needs all 3 blobs).
  // Component T-Parts only need buildSyntheticPartJson directly — see
  // generateSyntheticPartJsonForComponent above.
  private assembleSyntheticPartSearchData(row: SyntheticPartSearchRow): {
    PartJson: string;
    InventoryJson: string;
    pricingInfo: string;
  } {
    return {
      PartJson: JSON.stringify(this.buildSyntheticPartJson(row)),
      InventoryJson: JSON.stringify(this.buildSyntheticInventoryJson(row)),
      pricingInfo: JSON.stringify(this.buildSyntheticPricingJson(row)),
    };
  }

  // uomCode/uomDescriptor always resolve to the same text description (e.g. "EA") —
  // falls back to the raw numeric value only when it's not in uom_list
  private resolveUomCode(uom: number | null): string {
    const uomList: any[] = this.globalService.getDataFromStorage('uom_list') || [];
    const uomEntry = uomList.find((t: any) => t.EnumValue === uom);
    return uomEntry?.Description ?? String(uom ?? '');
  }

  private buildSyntheticPartJson(row: SyntheticPartSearchRow) {
    const uomCode = this.resolveUomCode(row.uom);
    const shipTypeStr: string | null = this.resolver.resolveShipTypeDescription(
      row.shipType as number | null,
    );

    return {
      partNumber: row.partNumber ?? null,
      partDescription: row.partDescription ?? null,
      uomCode,
      uomDescriptor: uomCode,
      legacyPart: null,
      manufacturerName: row.manufacturerName ?? null,
      manufacturerCode: row.manufacturerCode ?? null,
      mfgPartNumber: row.mfgPartNumber ?? null,
      productImgUrl: null,
      hazardClass: null,
      unNumber: null,
      // New T-parts are always non-stock at creation time
      shipLocation: 'NONSTOCK',
      snapshotOnHandQty: 0,
      snapshotOnOrderQty: 0,
      dcLocation: 'NONSTOCK',
      snapshotdcOnHandQty: 0,
      snapshotNatOnhandQty: 0,
      // Configurator parts use "0000" as the customer account in part search results
      customerAccount: '0000',
      customerPart: null,
      mfrBrandParent: null,
      mfrBrandDivision: null,
      mfrBrandProductLine: null,
      taxonomyCode1: row.taxonomyCode1 ?? null,
      taxonomyName1: row.taxonomyName1 ?? null,
      taxonomyCode2: row.taxonomyCode2 ?? null,
      taxonomyName2: row.taxonomyName2 ?? null,
      taxonomyCode3: row.taxonomyCode3 ?? null,
      taxonomyName3: row.taxonomyName3 ?? null,
      taxonomyCode4: row.taxonomyCode4 ?? null,
      taxonomyName4: row.taxonomyName4 ?? null,
      weightUOM: null,
      weight: 0,
      lengthUOM: null,
      length: 0,
      widthUOM: null,
      width: 0,
      heightUOM: null,
      height: 0,
      itemtype: null,
      ddpItemFlag: false,
      partType: row.partType ?? null,
      specificationDocUrl: null,
      lwScore: 0,
      lwDocId: null,
      buyStdPackQty: 0,
      buyMOQ: 0,
      buyMultiplierQty: 0,
      sellMOQ: '1',
      sellMultiplierQty: '1',
      hazardous: false,
      priceReady: false,
      shipType: shipTypeStr,
      partStatus: 'Active',
      marketingMemo: null,
      rank: 0,
      isGenericPart: false,
      genericPartList: null,
      sdsUrl: null,
      ownedBrand: false,
      isCuttable: false,
      bomType: null,
      isTechnicalHold: null,
      isReel: false,
      extensions: [{ name: 'null', value: 'null' }],
    };
  }

  private buildSyntheticInventoryJson(row: SyntheticPartSearchRow) {
    const uomCode = this.resolveUomCode(row.uom);

    return {
      partNumber: row.partNumber ?? null,
      uomCode,
      lineNumber: row.lineReference ?? null,
      supplies: [{ commonInventory: [], segmentedInventory: [], openPO: [], openSO: [] }],
    };
  }

  private buildSyntheticPricingJson(row: SyntheticPartSearchRow) {
    const uomCode = this.resolveUomCode(row.uom);
    const shipTypeStr: string | null = this.resolver.resolveShipTypeDescription(
      row.shipType as number | null,
    );
    const shipLocationNumber: string | null = this.resolver.getNumberFromLocationName(
      row.shipLocation,
    );

    return {
      lineReference: row.lineReference ?? null,
      partNumber: row.partNumber ?? null,
      pricingUom: uomCode,
      orderedQtyUomCode: uomCode,
      uomConversionFactor: 1,
      quantity: row.quantity ?? 1,
      pricingDate: null,
      supplierNumber: null,
      supplierSite: '',
      programNumber: '',
      projectNumber: null,
      shippingLocation: shipLocationNumber ?? '',
      internalSupplierLocation: '',
      baseUOMListPrice: null,
      listPrice: null,
      lowestResell: null,
      distributorNetPrice: null,
      transferCostSource: null,
      transferCostSourceName: null,
      transferCostRegistrationNum: null,
      transferCostEndDate: null,
      transferCostComponent: null,
      transferCostPercent: null,
      transferCostLocked: 'N',
      repCostSource: null,
      repCostSourceName: null,
      repCostRegistrationNum: null,
      repCostEndDate: null,
      repCostComponent: null,
      replacementCost: null,
      replacementCostType: null,
      repCostLocked: 'N',
      repCostNetTbl: null,
      repCostQtyBreak: 'null',
      IsSpecialCost: 'N',
      specialCostIdentifier: null,
      repCostStdCostLocSource: null,
      repCostStdCostLocSourceName: null,
      repCostStdCostLocRegistrationNum: null,
      repCostStdCostLocEndDate: null,
      repCostStdCostLocComponent: null,
      repCostStdCostLocCost: 0,
      repCostStdCostLocCostType: null,
      repCostStdCostLocLocked: 'N',
      repCostStdCostLocNetTbl: null,
      repCostStdCostLocQtyBreak: null,
      serviceLocation: null,
      repCostStdCostLocation: '',
      replacementCostUOM: '',
      replacementCostInUOM: null,
      averageCost: null,
      sourceAverageCostUOM: null,
      sourceAverageCost: null,
      sourceShipType: shipTypeStr ?? null,
      enteredCost: null,
      loadBaseCost: null,
      loadrepCost: null,
      baseCostSource: null,
      baseCostSourceName: null,
      baseCostRegistrationNum: null,
      baseCostEndDate: null,
      baseCostComponent: null,
      baseCost: 0,
      baseCostLocked: 'N',
      handlingAdderSource: null,
      handlingAdderSourceName: null,
      handlingAdderRegistrationNum: null,
      handlingAdderEndDate: null,
      handlingAdderComponent: null,
      handlingAdder: null,
      handlingAdderLocked: 'N',
      chargebkUnitSource: null,
      chargebkUnitSourceName: null,
      chargebkUnitRegistrationNum: null,
      chargebkUnitEndDate: null,
      chargebkUnitComponent: null,
      baseUOMChargebkUnit: null,
      chargebkUnit: null,
      chargebkUnitLocked: 'N',
      spaCostSource: null,
      spaCostSourceName: null,
      spaCostRegistrationNum: null,
      spaCostEndDate: null,
      spaCostComponent: null,
      baseUOMSpaCost: 0,
      spaCost: 0,
      spaCostLocked: 'N',
      spaQuantityUsage: null,
      spaQuantityRemaining: null,
      unitSpaClaim: null,
      spaClaimAccrualSource: null,
      spaClaimAccrualSourceName: null,
      spaClaimAccrualRegistrationNum: null,
      spaClaimAccrualEndDate: null,
      spaClaimAccrualComponent: null,
      spaClaimAccrual: null,
      varianceSource: null,
      varianceSourceName: null,
      varianceRegistrationNum: null,
      varianceEndDate: null,
      varianceComponent: null,
      varianceAdder: null,
      baseUOMStandardCost: 0,
      standardCost: 0,
      varianceLocked: 'N',
      sellPriceSource: 'No Price found',
      sellPriceSourceName: 'No Price found',
      sellPriceContractType: null,
      sellPriceRegistrationNum: '',
      sellPriceEndDate: '',
      sellPriceComponent: '',
      baseUOMSellPrice: null,
      sellPrice: null,
      sellPriceType: null,
      minimumPriceAllowedPercent: null,
      shipType: shipTypeStr ?? 'STK',
      priceRequestLineId: null,
      manufacturerCode: row.manufacturerCode ?? null,
      mfgPartNumber: row.mfgPartNumber ?? null,
      shipToCustomer: null,
      requestedSupplier: '',
      requestedPartNumber: row.partNumber ?? null,
      requestedItemType: null,
      requestedDDPItemFlag: true,
      manufacturerforguidance: row.manufacturerName ?? null,
      mfrPartNumberForGuidance: '',
      manufacturerPubPriceBasis: '',
      pgAdvertisedGPPercent: null,
      pgAdvertisedPrice: null,
      pgCustomerLevelNGP: null,
      pgCustomerSize: null,
      pgDefaultGPPercent: null,
      pgDefaultPrice: null,
      pgbaseUOMAdvertisedPrice: null,
      pgbaseUOMDefaultPrice: null,
      pgbaseUOMStretch: null,
      pgbaseUOMTarget: null,
      pgbaseUOMFloor: null,
      pgTarget: null,
      pgFloor: null,
      pgStretch: null,
      pgSourceRaw: null,
      pgMinGP: null,
      pgMaxGP: null,
      pgNGPBusinessUnit: null,
      pgNGPCountry: null,
      pgNGPCustomerSize: null,
      pgNGPCustType: null,
      pgNGPNationalFlag: null,
      pgNGPGeography: null,
      pgNonPriceSensitiveItemFlag: null,
      pgNonPriceSensitiveItemFloorMarginPercent: null,
      pgPriceMethodName: null,
      pgQuantityBreak1: null,
      pgQuantityBreak2: null,
      pgQuantityBreak3: null,
      pgQuantityBreak4: null,
      pgQuantityBreak5: null,
      pgQuantityBreak1Price: null,
      pgQuantityBreak2Price: null,
      pgQuantityBreak3Price: null,
      pgQuantityBreak4Price: null,
      pgQuantityBreak5Price: null,
      pgUncommonItemFlag: null,
      pgUncommonItemFloorMarginPercent: null,
      pgUnitCost: null,
      pgUnitPublishedPrice: null,
    };
  }

  // ── CPQ mappers ─────────────────────────────────────────────────────────────
  mapFgDetailsFromRequestHeader(requestHeader: any): any {
    return {
      ...requestHeader,
      ProjectId: requestHeader.programOrProjectID ?? null,
      Quantity:
        requestHeader.quantity &&
        !isNaN(Number(requestHeader.quantity)) &&
        Number(requestHeader.quantity) > 0
          ? Number(requestHeader.quantity)
          : 1,
      ShipSelction: requestHeader.shipSelection,
      EarliestAcceptDate: requestHeader?.earliestAcceptDate ?? null,
      RequiredOnSiteDate: requestHeader?.requiredOnsiteDate ?? null,
      WarehouseDate: requestHeader?.warehouseDate ?? null,
    };
  }
  mapFgDetailsDataFromCpq(fgDetails: any, cpQDetails: any): any {
    return {
      FinishedGoodId: fgDetails?.FinishedGoodId,
      ParentFinishedGoodId: cpQDetails?.saleOrderLineRefNumber ?? null,
      BOMEditAllowed: fgDetails?.BOMEditAllowed ?? null,
      BOMType: fgDetails?.BOMType,
      Cost: cpQDetails?.unitCost ?? null,
      AverageCost: cpQDetails?.averageCost ?? null,
      CutAssembly: fgDetails?.CutAssembly,
      Description: fgDetails?.Description,
      FGLeadTimeInDays: cpQDetails?.fgLeadTimeInDays ?? null,
      FgWorkReleaseDate: cpQDetails?.fgWorkReleaseDate ?? null,
      FGPartNumber: fgDetails?.FGPartNumber,
      FinishedGoodDocuments: fgDetails?.FinishedGoodDocuments,
      GPPercentage: cpQDetails?.gpPercentage ?? null,
      IsTPart: fgDetails?.IsTPart ?? false,
      OSPShipToId: cpQDetails?.componentShipTo ?? null,
      OSPSiteId: cpQDetails?.supplierSite ?? null,
      OpsApprovalObtained: fgDetails?.OpsApprovalObtained ?? null,
      PartType: fgDetails?.PartType,
      Price: cpQDetails?.unitPrice ?? null,
      ProjectId: cpQDetails?.programOrProjectID ?? null,
      Quantity:
        cpQDetails?.quantity &&
        !isNaN(Number(cpQDetails.quantity)) &&
        Number(cpQDetails.quantity) > 0
          ? Number(cpQDetails.quantity)
          : 1,
      ShipLocationIds: fgDetails?.ShipLocationIds,
      shipLocationId: cpQDetails?.shipLocation,
      ShipType: this.resolver.resolveShipTypeToEnumValue(cpQDetails?.shipType),
      Status: fgDetails?.Status ?? null,
      TransactiblePartNumber: cpQDetails?.transactablePartNumber ?? null,
      UOM: fgDetails?.UOM,
      WorkInstructionId: fgDetails?.WorkInstructionId,
      WorkInstructionName: fgDetails?.WorkInstructionName,
      WorkOrderRelease: fgDetails?.WorkOrderRelease,
      ShipSelction: cpQDetails?.shipSelection ?? null,
      ShipLocation: cpQDetails?.shipLocation ?? null,
      EarliestAcceptDate: cpQDetails?.earliestAcceptDate ?? null,
      RequiredOnSiteDate: cpQDetails?.requiredOnsiteDate ?? null,
      WarehouseDate: cpQDetails?.warehouseDate ?? null,
      CustLineNum: cpQDetails?.customerLineNumber ?? null,
      TransferFromShipLocation: cpQDetails?.transferFromShipLocation ?? null,
      Version: fgDetails?.Version,
      salesOrderLineNumber: cpQDetails?.salesOrderLineNumber,
      saleOrderLineRefNumber: cpQDetails?.saleOrderLineRefNumber,
      customerAccountNumber: cpQDetails?.customerAccountNumber,
      noteVasWorkInstruction: cpQDetails?.noteVasWorkInstruction ?? null,
      DefaultShipLocationId: fgDetails?.DefaultShipLocationId ?? null,
      shipLocationShipType: cpQDetails?.shipLocationShipType ?? 'Non-STK',
      TaxonomyId: fgDetails?.TaxonomyId ?? null,
      PartJson: fgDetails?.PartJson ?? null,
      TaskTypeId: fgDetails?.TaskTypeId ?? null,
      // cpQDetails.fgTaskDetail.taskType is the CODE (e.g. "MIXED"), not the display name — only
      // TaskTypeCode is refreshed from it; TaskTypeName (rendered to the user) stays VAS-sourced.
      TaskTypeCode: cpQDetails?.fgTaskDetail?.taskType ?? fgDetails?.TaskTypeCode ?? null,
      TaskTypeName: fgDetails?.TaskTypeName ?? null,
    };
  }

  mapFgComponentsDataFromCpq(fgComponents: any[], cpqDetails: any, maxDecimals: number): any[] {
    const cpqComponents = cpqDetails?.kitComponents;
    if (!Array.isArray(cpqComponents)) return fgComponents ?? [];

    // Scoped by hierarchy (root vs nested), not a flat PartNumber Map — the same PartNumber
    // can appear once at the root level and again inside a nested sub-assembly, and a flat
    // Map would silently collapse the two into one (see VcResolver.correlateCpqAndVasComponents).
    const { cpqRefToVasComponent } = this.resolver.correlateCpqAndVasComponents(
      cpqComponents,
      fgComponents,
    );

    const toISODate = (date: string | null | undefined) =>
      date && dayjs(date).isValid() ? dayjs(date).toISOString() : null;

    return cpqComponents.map((comp) => {
      const vas: any = cpqRefToVasComponent.get(comp.saleOrderLineRefNumber ?? '') ?? {};
      const extCost = this.calculator.calculateExtendedValue(comp.kitConfigQty, comp.unitCost);
      const extPrice = this.calculator.calculateExtendedValue(comp.kitConfigQty, comp.unitPrice);
      const extAverageCost = this.calculator.calculateExtendedValue(
        comp.kitConfigQty,
        comp.averageCost,
      );

      return {
        FGHeaderDetails: vas.FGHeaderDetails ?? {},
        FinishedGoodLineItemId: vas.FinishedGoodLineItemId ?? comp.saleOrderLineRefNumber ?? null,
        FinishedGoodId: vas.FinishedGoodId ?? null,
        ParentFinishedGoodId: vas.ParentFinishedGoodId ?? null,
        ParentFinishedGoodLineItemId: vas.ParentFinishedGoodLineItemId ?? null,
        LineNumber: comp.salesOrderLineNumber ?? 0,
        MFRPartNumber: vas.MFRPartNumber ?? null,
        ManufacturerId: vas.ManufacturerId ?? null,
        MDMShipType: vas.MDMShipType ?? null,
        PartNumber: comp.transactablePartNumber ?? null,
        InventoryType:
          comp.inventorySegment &&
          comp.inventorySegmentType &&
          comp.inventoryType == 'Restricted Inventory'
            ? 2
            : 1,
        InventorySegment: comp.inventorySegment,
        InventorySegmentType: comp.inventorySegmentType,
        FgResInventory:  vas.InventoryType,
        PartType: vas.PartType ?? null,
        Description: vas.Description ?? null,
        TaxonomyId: vas.TaxonomyId ?? null,
        IsTpart: vas.IsTpart ?? null,
        Created: vas.Created ?? null,
        Modified: vas.Modified ?? null,
        FinishedGoodLineItemDocuments: vas.FinishedGoodLineItemDocuments ?? [],
        ShipType: this.resolver.resolveShipTypeToEnumValue(comp.shipType ?? null),
        ShipLocationId: comp.shipLocation ?? null,
        ShipLocation: comp.shipLocation ?? null,
        UOM: vas.UOM ?? null,
        Quantity:
          comp.kitConfigQty != null && !isNaN(Number(comp.kitConfigQty))
            ? Number(comp.kitConfigQty)
            : null,
        UnitCost: comp.unitCost ?? null,
        UnitPrice: comp.unitPrice ?? null,
        AverageCost: comp.averageCost ?? null,
        ExtendedCost: extCost ?? null,
        ExtendedPrice: extPrice ?? null,
        ExtendedAverageCost: extAverageCost ?? null,
        BM:
          comp.unitCost != null && comp.unitPrice != null && comp.unitCost !== comp.unitPrice
            ? this.calculator.preciseRound(
                this.calculator.calculateMargin(comp.unitPrice, comp.unitCost),
                maxDecimals,
              )
            : null,
        QuotedCost: comp.supplierQuoteCost ?? null,
        QuotedUOM: this.resolver.resolveqUomTypeToEnumValue(comp.supplierQuoteUOM ?? null),
        QuotedEffectiveDate: toISODate(comp.supplierQuoteValidFrom) ?? null,
        SupplierSiteId: comp.supplierSite ?? null,
        SupplierQuoteNumber: comp.supplierQuoteNumber ?? null,
        SupplierQuoteValidTillDate: toISODate(comp.supplierQuoteValidTill) ?? null,
        CustomerPartNumber: comp.productInfo?.customerPart ?? vas.CustomerPartNumber ?? null,
        TransferFromShipLocation: comp.transferFromShipLocation ?? null,
        ManufacturerName: vas.ManufacturerName ?? null,
        ComponentShipTo: comp.componentShipTo ?? null,
        shipLocationShipType: comp.shipLocationShipType ?? 'Non-STK',
        StageGroupNumber: comp.fgTaskDetail?.stageGroupId ?? vas.StageGroupNumber ?? null,
        StageDependencies: comp.fgTaskDetail?.stageDependencies ?? vas.StageDependencies ?? null,
        TaskTypeId: vas.TaskTypeId ?? null,
        // comp.fgTaskDetail.taskType is the CODE (e.g. "STRIPE"), not the display name — only
        // TaskTypeCode is refreshed from it; TaskTypeName (rendered to the user) stays VAS-sourced.
        TaskTypeCode: comp.fgTaskDetail?.taskType ?? vas.TaskTypeCode ?? null,
        TaskTypeName: vas.TaskTypeName ?? null,
        LBRPartTypeTaskAttributes:
          comp.fgTaskDetail?.taskAttributes ?? vas.LBRPartTypeTaskAttributes ?? null,
        // Sourced from the EDIT_FG/CPQ payload only (no `vas` fallback) so a previously-saved
        // value carried by the host always wins over a stale VAS-side fetch.
        SupplierPOInfo: this.mapSupplierInfoFromCpq(comp.supplierInfo),
      };
    });
  }

  // ── Private helpers ─────────────────────────────────────────────────────────

  resolveSupplierNumber(supplierSiteId: string | null | undefined, siteList: any[]): string | null {
    if (!supplierSiteId) return null;
    return (
      siteList?.find(
        (s: any) => s.SupplierSiteId === supplierSiteId || s.SupplierNumber === supplierSiteId,
      )?.SupplierNumber ?? null
    );
  }

  // Outbound: VAS's internal SupplierPOInfo snapshot → the CPQ submit payload's `supplierInfo`
  // node (VAS-126/VAS-364). null when More Info was never opened for this component — nothing
  // to send.
  private buildSupplierInfoPayload(comp: any, siteList: any[]): any {
    const info: SupplierPoInfo | null = comp.SupplierPOInfo ?? null;
    if (!info) {
      return null;
    }
    return {
      supplierSiteCode: this.resolveSupplierNumber(comp.SupplierSiteId, siteList),
      poFreightTerm: info.FreightTerms ?? null,
      fobCode: info.FOBCode ?? null,
      fobDescription: info.FOB ?? null,
      urgentBuy: info.UrgentBuy ?? false,
      ccSalesOnPO: info.CCSalesOnPO ?? false,
      sendEDI: info.SendEDI ?? false,
      poPrepaymentRequired: info.POPrepaymentRequired ?? false,
      freightAccountNumber: info.FreightAccountNumber ?? null,
      thirdPartyAddress: info.FreightAddress ?? null,
      shipMethod: info.ShipMethod ?? null,
      revisedPromiseShipDate: info.RevisedPromiseShipDate ?? null,
      // No contact-ID source exists in VAS yet (GetSupplierInformation's Contacts carry no id) —
      // confirmed with the business to send null until that data becomes available.
      supplierContactID: null,
      supplierContact: info.ContactName ?? null,
      supplierContactEmail: info.Email ?? null,
      supplierContactPhone: info.PhoneNumber ?? null,
      addContactToSupplierMaster: info.AddContactToSupplierMaster ?? false,
    };
  }

  // Inbound: the reverse of buildSupplierInfoPayload() — an EDIT_FG/CPQ-echoed `supplierInfo`
  // node back into VAS's internal SupplierPOInfo shape. IsEdiEnabled/Contacts aren't part of the
  // CPQ wire contract, so they're left unset; a subsequent More Info fetch backfills them.
  private mapSupplierInfoFromCpq(supplierInfo: any): SupplierPoInfo | null {
    if (!supplierInfo) {
      return null;
    }
    return {
      FOB: supplierInfo.fobDescription ?? null,
      FOBCode: supplierInfo.fobCode ?? null,
      FreightTerms: supplierInfo.poFreightTerm ?? null,
      ShipMethod: supplierInfo.shipMethod ?? null,
      FreightAccountNumber: supplierInfo.freightAccountNumber ?? null,
      FreightAddress: supplierInfo.thirdPartyAddress ?? null,
      UrgentBuy: supplierInfo.urgentBuy ?? false,
      CCSalesOnPO: supplierInfo.ccSalesOnPO ?? false,
      SendEDI: supplierInfo.sendEDI ?? false,
      POPrepaymentRequired: supplierInfo.poPrepaymentRequired ?? false,
      RevisedPromiseShipDate: supplierInfo.revisedPromiseShipDate ?? null,
      ContactName: supplierInfo.supplierContact ?? null,
      Email: supplierInfo.supplierContactEmail ?? null,
      PhoneNumber: supplierInfo.supplierContactPhone ?? null,
      AddContactToSupplierMaster: supplierInfo.addContactToSupplierMaster ?? false,
    };
  }
}
